import React from "react";

export default function WhyUs() {
  return (
    <section className="why-us" dir="rtl" aria-labelledby="whyus-title">
      <style>{`
        :root {
          --accent: #1f6f3a;
          --muted: #6b7a6b;
          --card-bg: rgba(255, 255, 255, 0.95);
          --glass: rgba(255, 255, 255, 0.1);
        }

        .why-us {
          padding: 90px 20px;
          background: linear-gradient(180deg, rgba(47,63,47,0.05), #fff);
          font-family: "Noto Sans Arabic", Arial, sans-serif;
        }

        .why-us__inner {
          max-width: 1150px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 380px;
          gap: 40px;
          align-items: start;
        }

        /* العنوان الرئيسي */
        .mainTitle {
          text-align: center;
          margin-bottom: 50px;
        }
        .mainTitle h2 {
          display: inline-block;
          position: relative;
          font-size: 32px;
          font-weight: 800;
          color: #143414;
        }
        .mainTitle h2::before, .mainTitle h2::after {
          content: "";
          position: absolute;
          top: 50%;
          width: 35px;
          height: 2px;
          background-color: var(--accent);
        }
        .mainTitle h2::before {
          right: 100%;
          margin-right: 15px;
        }
        .mainTitle h2::after {
          left: 100%;
          margin-left: 15px;
        }

        .why-us__content {
          padding: 10px;
        }
        .why-us__title {
          color: #0b2e13;
          font-size: 26px;
          margin-bottom: 14px;
          line-height: 1.3;
        }
        .why-us__desc {
          color: var(--muted);
          margin-bottom: 25px;
          font-size: 15.5px;
          line-height: 1.8;
        }

        /* المميزات */
        .features {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 18px;
        }
        .feature {
          background: var(--card-bg);
          border-radius: 14px;
          padding: 16px;
          display: flex;
          gap: 12px;
          align-items: flex-start;
          box-shadow: 0 6px 18px rgba(15, 23, 15, 0.05);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .feature:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 26px rgba(15, 23, 15, 0.08);
        }
        .feature__icon {
          min-width: 44px;
          min-height: 44px;
          border-radius: 10px;
          background: var(--glass);
          display: grid;
          place-items: center;
          color: var(--accent);
        }
        .feature__title {
          font-weight: 700;
          color: #163b20;
          margin: 0 0 5px 0;
          font-size: 15px;
        }
        .feature__text {
          margin: 0;
          color: var(--muted);
          font-size: 13.5px;
          line-height: 1.5;
        }

        /* الشريط الجانبي */
        .aside {
          background: linear-gradient(180deg, rgba(31,111,58,0.05), rgba(255,255,255,0.08));
          padding: 26px;
          border-radius: 16px;
          border: 1px solid rgba(31,111,58,0.15);
          color: #0b2e13;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .aside__meta {
          background: var(--card-bg);
          padding: 14px;
          border-radius: 10px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.05);
        }
        .aside__name {
          font-size: 18px;
          font-weight: 800;
          margin: 0 0 6px;
          color: #06220f;
        }
        .aside__title {
          font-size: 13px;
          color: var(--muted);
          margin: 0;
        }
        .cta {
          display: inline-block;
          background: var(--accent);
          color: #fff;
          padding: 10px 16px;
          border-radius: 10px;
          text-decoration: none;
          font-weight: 600;
          font-size: 14px;
          transition: 0.3s;
          box-shadow: 0 8px 20px rgba(31,111,58,0.18);
        }
        .cta:hover {
          background: #195c2f;
        }

        /* Responsive */
        @media (max-width: 920px) {
          .why-us__inner {
            grid-template-columns: 1fr;
          }
          .aside {
            margin-top: 40px;
          }
        }

        @media (max-width: 600px) {
          .mainTitle h2 {
            font-size: 26px;
          }
          .why-us {
            padding: 70px 15px;
          }
          .why-us__title {
            font-size: 22px;
          }
        }
      `}</style>

      <div className="mainTitle">
        <h2>لماذا تختار مكتبنا؟</h2>
      </div>

      <div className="why-us__inner">
        {/* المحتوى الأساسي */}
        <div className="why-us__content">
          <h2 id="whyus-title" className="why-us__title">
            خبرة، تخصص، ونتائج مضمونة
          </h2>
          <p className="why-us__desc">
            نقدم تمثيلاً قانونيًا متكاملاً بقيادة الأستاذ{" "}
            <strong>محمد إبراهيم أبو عدبة</strong> — محامٍ بالنقض والإدارية
            العليا، متخصِّص في قضايا التأمينات والمعاشات، بخلفية أكاديمية متميزة
            في التجارة الدولية.
          </p>

          <div className="features" role="list">
            {[
              {
                title: "تمثيل أمام النقض والإدارية العليا",
                text: "سنقف إلى جانبك أمام أعلى درجات التقاضي بخبرة معمَّقة ومتابعة دقيقة لكل خطوة قضائية.",
                icon: "M12 2L4 5v6c0 5 4 9 8 9s8-4 8-9V5l-8-3z",
              },
              {
                title: "خبير في التأمينات والمعاشات",
                text: "تحليل دقيق لقضايا الضمان الاجتماعي والمستحقات القانونية لضمان حقوقك كاملة.",
                icon: "M3 7h18M7 7v10a4 4 0 004 4h2a4 4 0 004-4V7",
              },
              {
                title: "نهج شخصي واستراتيجية واضحة",
                text: "نضع خطة قانونية مخصّصة لكل عميل، مع شفافية كاملة في الإجراءات والتكاليف.",
                icon: "M12 3v18M3 12h18",
              },
              {
                title: "خلفية أكاديمية في التجارة الدولية",
                text: "ماجستير في التجارة الدولية يتيح تقديم استشارات متخصصة في القضايا العابرة للحدود.",
                icon: "M4 6h16M4 12h10M4 18h7",
              },
            ].map((item, i) => (
              <article key={i} className="feature" role="listitem" tabIndex="0">
                <div className="feature__icon" aria-hidden="true">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d={item.icon}
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="feature__title">{item.title}</h3>
                  <p className="feature__text">{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* الجانب الأيمن */}
        <aside className="aside" aria-labelledby="aside-name">
          <div className="aside__meta">
            <p id="aside-name" className="aside__name">
              محمد إبراهيم أحمد
            </p>
            <p className="aside__title">
              محامٍ بالنقض والإدارية العليا — صاحب مكتب بمدينة نصر، القاهرة
            </p>
          </div>

          <div style={{ fontSize: 14, color: "#163b20" }}>
            <strong>التخصص:</strong> التأمينات والمعاشات، القضايا المدنية
            والتجارية، والنزاعات الإدارية.
          </div>

          <p style={{ margin: 0, fontSize: 13.5, color: "#234a2b" }}>
            نلتزم بالاحترافية والسرية في التعامل، ونسعى لتقديم حلول قانونية
            عملية تحقق أفضل النتائج لعملائنا.
          </p>

          <a className="cta" href="/contact">
            اطلب استشارة
          </a>
        </aside>
      </div>
    </section>
  );
}
